import { useState } from "react"
import { Head, Link, router } from "@inertiajs/react"
import { BookOpen, ChevronLeft, ChevronRight, Download, Search } from "lucide-react"
import { AdminLayout } from "../components/AdminLayout"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"
import { BOOK_STAGE, StageChip, formatDate } from "./components/bookStage"

// "2026-10" moved by `step` months.
function shiftMonth(month, step) {
  const [year, number] = month.split("-").map(Number)
  const date = new Date(year, number - 1 + step, 1)
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`
}

function monthLabel(month) {
  const [year, number] = month.split("-").map(Number)
  return new Date(year, number - 1, 1).toLocaleDateString("en-US", { month: "long", year: "numeric" })
}

// Every Kingdom Factors book and where it is, plus the books published in a
// month (first sent to print), so Jen can bill coaches outside the app.
export default function BooksIndex({ currentUser, books = [], month, published = [] }) {
  const [stage, setStage] = useState("all")
  const [query, setQuery] = useState("")

  const q = query.trim().toLowerCase()
  const shown = books.filter((book) =>
    (stage === "all" || book.stage === stage) &&
    (!q || book.coach.toLowerCase().includes(q) || book.title.toLowerCase().includes(q)))
  const goToMonth = (next) => router.get("/app/admin/books", { month: next }, { preserveScroll: true, preserveState: true })

  return (
    <AdminLayout currentUser={currentUser}>
      <Head title="All books - Kingdom Factor" />
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">All books</h1>
          <p className="text-zinc-600">Every Kingdom Factors book and where it is. Open a book to read its chapters, decide its flags, or unlock it for a fix.</p>
        </div>

        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center gap-3 space-y-0">
            <CardTitle className="text-lg">Published in</CardTitle>
            <div className="flex items-center gap-1">
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Month before" onClick={() => goToMonth(shiftMonth(month, -1))}>
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="min-w-36 text-center font-medium">{monthLabel(month)}</span>
              <Button size="icon" variant="ghost" className="h-8 w-8" aria-label="Month after" onClick={() => goToMonth(shiftMonth(month, 1))}>
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
            <Button asChild size="sm" variant="outline" className="ml-auto">
              <a href={`/app/admin/books/published?month=${month}`}><Download className="h-4 w-4" /> Download CSV</a>
            </Button>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-zinc-600">
              <b className="text-zinc-900">{published.length} {published.length === 1 ? "book" : "books"}</b> first sent to print this month. A book unlocked and sent again counts in the month it was first sent.
            </p>
            {published.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead className="text-xs text-zinc-500">
                    <tr><th className="py-1 pr-4 font-medium">Coach</th><th className="py-1 pr-4 font-medium">Email</th><th className="py-1 pr-4 font-medium">Book</th><th className="py-1 font-medium">Published</th></tr>
                  </thead>
                  <tbody>
                    {published.map((book) => (
                      <tr key={book.id} className="border-t">
                        <td className="py-2 pr-4">{book.coach}</td>
                        <td className="py-2 pr-4 text-zinc-600">{book.email}</td>
                        <td className="py-2 pr-4"><Link href={`/app/admin/books/${book.id}`} className="hover:underline">{book.title}</Link></td>
                        <td className="py-2 whitespace-nowrap">{formatDate(book.publishedAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-2">
          {["all", ...Object.keys(BOOK_STAGE)].map((key) => {
            const count = key === "all" ? books.length : books.filter((book) => book.stage === key).length
            return (
              <button key={key} type="button" onClick={() => setStage(key)}
                className={cn("rounded-full border px-3 py-1 text-sm", stage === key ? "border-zinc-900 bg-zinc-900 text-white" : "bg-white text-zinc-700 hover:bg-zinc-50")}>
                {key === "all" ? "All" : BOOK_STAGE[key].label} <span className="opacity-70">{count}</span>
              </button>
            )
          })}
          <div className="relative ml-auto w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-zinc-400" />
            <Input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search coach or book" className="pl-8" />
          </div>
        </div>

        {shown.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-sm text-zinc-600">
              <BookOpen className="mx-auto mb-2 h-6 w-6 text-zinc-400" />
              {books.length === 0 ? "No coach has started a book yet." : "No book matches."}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="overflow-x-auto p-0">
              <table className="w-full text-left text-sm">
                <thead className="border-b bg-zinc-50 text-xs text-zinc-500">
                  <tr>
                    <th className="px-4 py-2 font-medium">Book</th>
                    <th className="px-4 py-2 font-medium">Stage</th>
                    <th className="px-4 py-2 font-medium">Chapters approved</th>
                    <th className="px-4 py-2 font-medium">Leaders approved</th>
                    <th className="px-4 py-2 font-medium">Last change</th>
                    <th className="px-4 py-2 font-medium">Published</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((book) => (
                    <tr key={book.id} className="border-b last:border-0 hover:bg-zinc-50">
                      <td className="px-4 py-3">
                        <Link href={`/app/admin/books/${book.id}`} className="font-medium text-zinc-900 hover:underline">{book.title}</Link>
                        <div className="text-xs text-zinc-500">{book.coach}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          <StageChip stage={book.stage} />
                          {book.seriousFlags > 0 && (
                            <span className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-semibold text-white whitespace-nowrap">
                              {book.seriousFlags} serious {book.seriousFlags === 1 ? "flag" : "flags"}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">{book.chaptersApproved} of {book.chapters}</td>
                      <td className="px-4 py-3 whitespace-nowrap">{book.leadersApproved} of {book.chapters}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-zinc-600">{formatDate(book.updatedAt)}</td>
                      <td className="px-4 py-3 whitespace-nowrap text-zinc-600">{formatDate(book.publishedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
